/**
 * ApprovalBadge — status pill for approval workflows.
 *
 * Used for room document approvals, message approvals, and any other
 * 5-state approval lifecycle (draft → pending → approved/rejected/withdrawn).
 *
 * Colors:
 *   pending   → amber    (awaiting decision)
 *   approved  → emerald  (decision: approved)
 *   rejected  → rose     (decision: rejected)
 *   withdrawn → gray     (cancelled by submitter)
 *   draft     → gray/muted (not yet submitted)
 *
 * @layer atom
 * @package @plerity/ui-core
 */
import { memo } from "react";
import { cn } from "../utils/cn";
import type { ApprovalBadgeProps, ApprovalStatus } from "./ApprovalBadge.types";

const STATUS_CONFIG: Record<ApprovalStatus, { label: string; classes: string }> = {
  pending: {
    label: "Pending",
    classes:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-700/50",
  },
  approved: {
    label: "Approved",
    classes:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/50",
  },
  rejected: {
    label: "Rejected",
    classes:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 border border-rose-200 dark:border-rose-700/50",
  },
  // withdrawn / draft: literal gray ramp — do NOT use `bg-surface-subtle` or
  // `border-[var(--color-border-default)]`. Those emit `var(--color-*)` with NO
  // fallback and are stripped from the React SPA apps' Tailwind v4 build. The
  // literal ramp keeps fallbacks so dark mode renders true-dark.
  withdrawn: {
    label: "Withdrawn",
    classes:
      "bg-gray-100 text-gray-600 border border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300",
  },
  draft: {
    label: "Draft",
    classes:
      "bg-gray-100 text-gray-500 border border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-400",
  },
};

export const ApprovalBadge = memo(function ApprovalBadge({ status, className }: ApprovalBadgeProps) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <output
      className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", config.classes, className)}
      aria-label={`Approval status: ${config.label}`}
    >
      {config.label}
    </output>
  );
});

ApprovalBadge.displayName = "ApprovalBadge";
